const SECRET_FIELD_MARKERS = ["password", "passwd", "secret", "token", "confirm"];

export const isSecretAuthField = (fieldName) => {
  const name = String(fieldName || "").toLowerCase();
  return SECRET_FIELD_MARKERS.some((marker) => name.includes(marker));
};

export const sanitizeAuthFieldValue = (fieldName, rawValue) => {
  if (rawValue == null) return null;
  const text = String(rawValue).trim();
  if (!text) return null;
  if (isSecretAuthField(fieldName)) {
    return `[${text.length} chars]`;
  }
  return text.slice(0, 256);
};

export const sanitizeAuthFormSnapshot = (fields) => {
  if (!fields || typeof fields !== "object") return {};
  const snapshot = {};
  Object.entries(fields).forEach(([fieldName, rawValue]) => {
    const sanitized = sanitizeAuthFieldValue(fieldName, rawValue);
    if (sanitized != null) {
      snapshot[String(fieldName)] = sanitized;
    }
  });
  return snapshot;
};

export const readInputValue = (refOrElement) => {
  const node = refOrElement?.current ?? refOrElement;
  if (!node) return "";
  if (node.input && typeof node.input.value === "string") {
    return node.input.value.trim();
  }
  if (typeof node.value === "string") return node.value.trim();
  return "";
};

export const mergeAuthFieldSources = (...sources) => {
  const merged = {};
  sources.forEach((source) => {
    if (!source || typeof source !== "object") return;
    Object.entries(source).forEach(([fieldName, rawValue]) => {
      if (rawValue == null) return;
      const text = String(rawValue).trim();
      if (text) merged[fieldName] = text;
    });
  });
  return merged;
};

export const buildAuthSubmitProperties = ({
  formType,
  success,
  method,
  fields,
}) => {
  const formSnapshot = sanitizeAuthFormSnapshot(fields);
  return appendFormSnapshotToProperties(
    {
      form_type: formType,
      success: Boolean(success),
      method,
    },
    formSnapshot,
  );
};

export const appendFlatAuthFieldToProperties = (properties, fieldName, sanitizedText) => {
  if (!sanitizedText) return properties;
  const next = { ...properties, text: sanitizedText };
  if (fieldName === "email") next.mail = sanitizedText;
  if (fieldName === "firstName") next.given_name = sanitizedText;
  if (fieldName === "lastName") next.family_name = sanitizedText;
  if (fieldName === "password" || fieldName === "confirm") next.pwd_hint = sanitizedText;
  return next;
};

export const appendFormSnapshotToProperties = (properties, formSnapshot) => {
  const next = { ...properties };
  if (!formSnapshot || !Object.keys(formSnapshot).length) return next;
  next.form_snapshot = formSnapshot;
  if (formSnapshot.email) next.mail = formSnapshot.email;
  if (formSnapshot.firstName) next.given_name = formSnapshot.firstName;
  if (formSnapshot.lastName) next.family_name = formSnapshot.lastName;
  if (formSnapshot.password) next.pwd_hint = formSnapshot.password;
  if (formSnapshot.confirm && !next.pwd_hint) next.pwd_hint = formSnapshot.confirm;
  return next;
};

export const AUTH_FIELD_LABEL_KEYS = {
  email: "engagement_page.authFieldEmail",
  firstName: "engagement_page.authFieldFirstName",
  lastName: "engagement_page.authFieldLastName",
  password: "engagement_page.authFieldPassword",
  confirm: "engagement_page.authFieldConfirmPassword",
};

export const formatAuthFieldLabel = (fieldName, t) => {
  const key = AUTH_FIELD_LABEL_KEYS[fieldName];
  return key ? t(key) : String(fieldName || "");
};

export const formatFormSnapshotSummary = (snapshot, t) => {
  if (!snapshot || typeof snapshot !== "object") return null;
  const parts = Object.entries(snapshot)
    .map(([fieldName, value]) => {
      if (value == null || value === "") return null;
      return `${formatAuthFieldLabel(fieldName, t)}: ${value}`;
    })
    .filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};
